import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, X, CalendarClock, FileText, Archive, Send, ArrowRight, ArrowLeft as ArrowLeftIcon, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";
import { useEngine } from "@/engine/store";
import { euro, fmtDate, lotFinance, weightedProgress, impactedLots, addDays, lotIsLate, COMPANY_DOC_TYPES, CENTRAL_FLOW, CENTRAL_STATUS, INVOICE_TYPES, ttc } from "@/engine/model";
import { Chip, Bar, LotProgressList, ComplianceBadge, Gantt, AuditList, History, RoleTag, PhotoField, PhotoThumb } from "@/engine/shared";
import { Card, Btn, Pill, Field, inputCls, SectionTitle, Placeholder, Segmented } from "../ui";
import { MOE_PHASES } from "@/data/partnerConfig";
import { usePartner } from "../PartnerContext";
import { PhaseStepper } from "./PartnerProjects";
import { ContractsPanel } from "./ContractsPanel";

const PETROL = "#284E55";

// ---------- Confirmation centrée (transition de phase) ----------
const PhaseConfirmModal = ({ open, title, body, confirmLabel, onConfirm, onCancel, testid }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-navy/40 backdrop-blur-[2px]" onClick={onCancel} data-testid={`${testid}-backdrop`} />
      <div data-testid={testid} role="dialog" aria-modal="true" className="relative z-[9999] w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="font-display text-lg font-medium text-navy">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-stone-500">{body}</p>
        <div className="mt-5 flex gap-2">
          <Btn testid={`${testid}-confirm`} onClick={onConfirm}>{confirmLabel}</Btn>
          <Btn variant="ghost" testid={`${testid}-cancel`} onClick={onCancel}>Annuler</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------- Transition manuelle de phase (Vue d'ensemble) ----------
const PhaseTransitionCard = ({ project }) => {
  const engine = useEngine();
  const { can } = usePartner();
  const [pending, setPending] = useState(null); // "forward" | "backward" | null
  const idx = MOE_PHASES.indexOf(project.phase);
  const isFirst = idx <= 0;
  const isLast = idx >= MOE_PHASES.length - 1;
  const editable = can("projects.edit");

  const run = (direction) => {
    engine.transitionPhase(project.ref, direction);
    toast.success(direction === "forward" ? `Étape validée : passage à « ${MOE_PHASES[idx + 1]} »` : `Retour à l'étape « ${MOE_PHASES[idx - 1]} »`);
    setPending(null);
  };

  return (
    <Card className="p-5" testid="phase-transition-card">
      <SectionTitle right={<Pill tone="petrol">{project.phase}</Pill>}>Étape du projet</SectionTitle>
      <PhaseStepper phase={project.phase} />
      {editable ? (
        <div className="mt-5 flex flex-wrap gap-2">
          <Btn testid="phase-transition-forward" disabled={isLast} onClick={() => setPending("forward")}>
            Valider cette étape et passer à la suivante <ArrowRight className="h-4 w-4" />
          </Btn>
          <Btn variant="ghost" testid="phase-transition-backward" disabled={isFirst} onClick={() => setPending("backward")}>
            <ArrowLeftIcon className="h-4 w-4" /> Revenir à l'étape précédente
          </Btn>
        </div>
      ) : (
        <p className="mt-4 text-xs text-stone-400">Seuls les membres habilités à modifier les projets peuvent faire évoluer l'étape.</p>
      )}
      {project.phaseHistory?.length > 0 && (
        <div className="mt-6">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400"><HistoryIcon className="h-3.5 w-3.5" /> Historique des étapes</p>
          <ul data-testid="phase-history-list" className="space-y-1.5 border-l-2 border-stone-200 pl-3 text-xs text-stone-600">
            {project.phaseHistory.map((h) => (
              <li key={h.id} data-testid={`phase-history-${h.id}`}>
                <span className="text-stone-400">{h.changedAt ? fmtDate(h.changedAt.slice(0, 10)) : ""}</span>{" "}
                {h.direction === "forward" ? "Passage" : "Retour"} de « {h.fromPhase} » à « {h.toPhase} » — {h.changedByName || "Utilisateur"}
              </li>
            ))}
          </ul>
        </div>
      )}
      <PhaseConfirmModal
        open={pending === "forward"}
        testid="phase-confirm-forward"
        title="Valider cette étape ?"
        body={`Le projet passera de « ${project.phase} » à « ${MOE_PHASES[idx + 1]} ». Le client verra cette progression immédiatement dans son Espace Client.`}
        confirmLabel="Valider et passer à la suivante"
        onConfirm={() => run("forward")}
        onCancel={() => setPending(null)}
      />
      <PhaseConfirmModal
        open={pending === "backward"}
        testid="phase-confirm-backward"
        title="Revenir à l'étape précédente ?"
        body={`Le projet reviendra de « ${project.phase} » à « ${MOE_PHASES[idx - 1]} ». Cette action sera tracée dans l'historique.`}
        confirmLabel="Confirmer le retour"
        onConfirm={() => run("backward")}
        onCancel={() => setPending(null)}
      />
    </Card>
  );
};

// ---------- Vue d'ensemble : ce qui nécessite l'attention du Partenaire ----------
export const EngineAttention = ({ project }) => {
  const engine = useEngine();
  const items = [];
  project.progressDeclarations.filter((p) => p.status === "a_verifier").forEach((p) => items.push({ id: p.id, text: `Avancement à vérifier — ${project.lots.find((l) => l.id === p.lotId)?.label} déclare ${p.declared} %`, tab: "Chantier" }));
  project.invoices.filter((i) => i.status === "a_verifier").forEach((i) => items.push({ id: i.id, text: `Facture à vérifier — ${i.number} (${euro(i.amountHT)} HT)`, tab: "Finances" }));
  project.invoices.filter((i) => i.status === "contestee").forEach((i) => items.push({ id: i.id, text: `Facture contestée par le client — ${i.number} : ${i.contest?.reason}`, tab: "Finances", alert: true }));
  project.quotes.filter((q) => ["depose", "a_examiner", "retenu"].includes(q.status)).forEach((q) => items.push({ id: q.id, text: `Devis à examiner — ${project.lots.find((l) => l.id === q.lotId)?.label} · ${engine.company(q.companyId)?.name}`, tab: "Professionnels & devis" }));
  project.lots.filter((l) => l.companyId && !engine.compliance(l.companyId).ok).forEach((l) => items.push({ id: l.id, text: `Intervention bloquée — ${l.label} · ${engine.company(l.companyId).name} : documents à régulariser`, tab: "Professionnels & devis", alert: true }));
  project.lots.filter(lotIsLate).forEach((l) => items.push({ id: `late${l.id}`, text: `Retard planning — ${l.label} (fin prévue ${fmtDate(l.end)}, validé ${l.validatedProgress} %)`, tab: "Planning" }));
  project.observations.filter((o) => ["signale", "a_verifier"].includes(o.status)).forEach((o) => items.push({ id: o.id, text: `Observation ${o.status === "signale" ? "signalée à affecter" : "à vérifier"} — ${o.title}`, tab: "Chantier" }));
  return items;
};

export const OverviewEngine = ({ project, setTab }) => {
  const items = EngineAttention({ project });
  const global = weightedProgress(project);
  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <div className="lg:col-span-5"><PhaseTransitionCard project={project} /></div>
      <Card className="p-5 lg:col-span-3" testid="engine-attention">
        <SectionTitle right={<Pill tone="copper">{items.length} points</Pill>}>Nécessite votre attention</SectionTitle>
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id}>
              <button data-testid={`attention-${it.id}`} onClick={() => setTab(it.tab)} className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors ${it.alert ? "border-red-100 bg-red-50/40 hover:border-red-300" : "border-[#EDF0F1] hover:border-petrol/40"}`}>
                <AlertTriangle className={`h-4 w-4 shrink-0 ${it.alert ? "text-red-600" : "text-cognac"}`} />
                <span className="flex-1 font-semibold text-navy">{it.text}</span>
                <span className="text-[11px] text-stone-400">{it.tab}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="p-5 lg:col-span-2" testid="engine-progress">
        <SectionTitle right={<span className="font-display text-3xl font-medium text-petrol" data-testid="global-progress">{global} %</span>}>Avancement du chantier</SectionTitle>
        <Bar value={global} color={PETROL} h="h-3" />
        <p className="mt-2 text-[11px] text-stone-400">Indicateur de progression physique, pondéré par le montant des lots (configurable). Distinct de la progression financière.</p>
        <div className="mt-5"><LotProgressList project={project} mode="partenaire" color={PETROL} testid="partner-lot-progress" /></div>
      </Card>
    </div>
  );
};

// ---------- Professionnels & devis : lots, entreprises, conformité, workflow devis ----------
const RequestQuoteForm = ({ project, engine, backend, onRequested }) => {
  const [catalog, setCatalog] = useState({ lots: [], etudes: [] });
  const [companies, setCompanies] = useState([]);
  const [catalogCode, setCatalogCode] = useState("");
  const [companyIds, setCompanyIds] = useState([]);
  const [comment, setComment] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!backend || !open) return;
    engine.catalog().then(setCatalog).catch(() => toast.error("Impossible de charger le catalogue des lots."));
    engine.companiesDirectory().then(setCompanies).catch(() => toast.error("Impossible de charger les entreprises."));
  }, [backend, open, engine]);

  if (!backend) return null;

  const toggleCompany = (id) => setCompanyIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const submit = () => {
    if (!catalogCode || companyIds.length === 0) { toast.error("Sélectionnez un lot ou une étude, et au moins une entreprise."); return; }
    engine.requestQuotes(project.ref, catalogCode, companyIds, comment);
    toast.success(`Demande de devis envoyée à ${companyIds.length} entreprise${companyIds.length > 1 ? "s" : ""}.`);
    setCatalogCode(""); setCompanyIds([]); setComment(""); setOpen(false);
    onRequested?.();
  };

  return (
    <Card className="p-5" testid="request-quote-card">
      <SectionTitle right={!open && <Btn testid="request-quote-open" className="px-3.5 py-2 text-xs" onClick={() => setOpen(true)}>Demander un devis</Btn>}>Demande de devis</SectionTitle>
      {!open ? (
        <p className="text-sm text-stone-500">Sélectionnez un lot ou une étude du catalogue CozyHome et une ou plusieurs entreprises à consulter.</p>
      ) : (
        <div className="space-y-3">
          <Field label="Lot ou étude">
            <select data-testid="request-quote-catalog" className={inputCls} value={catalogCode} onChange={(e) => setCatalogCode(e.target.value)}>
              <option value="">Sélectionner…</option>
              <optgroup label="Lots travaux">
                {catalog.lots.map((l) => <option key={l.code} value={l.code}>Lot {l.code} — {l.label}</option>)}
              </optgroup>
              <optgroup label="Études & prestations techniques">
                {catalog.etudes.map((e) => <option key={e.code} value={e.code}>Étude {e.code} — {e.label}</option>)}
              </optgroup>
            </select>
          </Field>
          <Field label="Entreprises à consulter">
            <div className="flex flex-wrap gap-2">
              {companies.map((c) => (
                <button key={c.id} type="button" data-testid={`request-quote-company-${c.id}`} onClick={() => toggleCompany(c.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${companyIds.includes(c.id) ? "border-petrol bg-petrol-tint text-petrol" : "border-[#D9E0E2] text-stone-500 hover:border-petrol/50"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Commentaire (optionnel)">
            <textarea data-testid="request-quote-comment" className={inputCls} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Précisions à transmettre à l'entreprise…" />
          </Field>
          <div className="flex gap-2">
            <Btn testid="request-quote-submit" onClick={submit}>Envoyer la demande</Btn>
            <Btn variant="ghost" testid="request-quote-cancel" onClick={() => setOpen(false)}>Annuler</Btn>
          </div>
        </div>
      )}
    </Card>
  );
};

const FEE_FORMULA_LABELS = { ESSENTIEL: "Essentiel", COMPLETE: "Complète" };

const FinancialRecap = ({ project, engine, backend }) => {
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    if (!backend) return;
    engine.financialSummary(project.ref).then(setSummary).catch(() => toast.error("Impossible de charger le récapitulatif financier."));
  }, [backend, engine, project.ref]);

  useEffect(() => { load(); }, [load, project.quotes.length]);

  if (!backend || !summary) return null;

  return (
    <Card className="p-5" testid="financial-recap">
      <SectionTitle>Récapitulatif financier du projet</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-petrol-bg p-3 text-sm"><p className="text-[11px] font-bold uppercase text-stone-400">Total travaux HT</p><p className="mt-1 font-display text-lg font-semibold text-navy" data-testid="recap-travaux">{euro(summary.total_travaux_ht)}</p></div>
        <div className="rounded-xl bg-petrol-bg p-3 text-sm"><p className="text-[11px] font-bold uppercase text-stone-400">Total études HT</p><p className="mt-1 font-display text-lg font-semibold text-navy" data-testid="recap-etudes">{euro(summary.total_etudes_ht)}</p></div>
        <div className="rounded-xl bg-petrol-bg p-3 text-sm"><p className="text-[11px] font-bold uppercase text-stone-400">Coût projet HT hors honoraires</p><p className="mt-1 font-display text-lg font-semibold text-navy" data-testid="recap-hors-honoraires">{euro(summary.cout_hors_honoraires_ht)}</p></div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {Object.entries(summary.simulations).map(([formula, s]) => (
          <div key={formula} data-testid={`recap-simulation-${formula}`} className={`rounded-xl border p-4 text-sm ${summary.fee_formula === formula ? "border-petrol bg-petrol-tint" : "border-[#E3E8E9]"}`}>
            <p className="flex items-center justify-between font-semibold text-navy">Formule {FEE_FORMULA_LABELS[formula]} {summary.fee_formula === formula && <Pill tone="petrol">Formule retenue</Pill>}</p>
            <p className="mt-1 text-xs text-stone-500">Coût travaux : {euro(summary.total_travaux_ht)} · Études : {euro(summary.total_etudes_ht)}</p>
            <p className="text-xs text-stone-500">Honoraires {FEE_FORMULA_LABELS[formula]} : {euro(s.honoraires_ht)} HT ({s.pct} %{s.min > 0 ? `, min. ${euro(s.min)}` : ""})</p>
            <p className="mt-1 font-display text-lg font-semibold text-navy">Total projet {FEE_FORMULA_LABELS[formula]} : {euro(s.total_projet_ht)} HT</p>
            {summary.fee_formula !== formula && (
              <Btn variant="soft" className="mt-2 px-3 py-1.5 text-xs" testid={`recap-contractualiser-${formula}`} onClick={() => { engine.setProjectFeeFormula(project.ref, formula); toast.success(`Formule ${FEE_FORMULA_LABELS[formula]} contractualisée sur ce dossier.`); }}>Contractualiser cette formule</Btn>
            )}
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-stone-400">Le calcul évolue automatiquement à chaque devis ajouté, remplacé ou retiré. La formule contractualisée n'est jamais modifiée automatiquement.</p>
    </Card>
  );
};

export const LotsTab = ({ project }) => {
  const engine = useEngine();
  const backend = engine.mode === "BACKEND";
  const [confirming, setConfirming] = useState(null);
  const next = { depose: ["a_examiner", "Examiner"], a_examiner: ["retenu", "Retenir"], retenu: ["accepte", "Accepter / signer"], accepte: ["preparation", "Préparer l'intervention"] };
  const groupsFor = (category) => project.lots.filter((l) => (l.category || "travaux") === category)
    .map((l) => ({ lot: l, quotes: project.quotes.filter((q) => q.lotId === l.id) }))
    .filter((g) => g.quotes.length > 0);
  const travauxGroups = groupsFor("travaux");
  const etudeGroups = groupsFor("etude");

  const requestFinalStatus = (q, lot, status, nextLabel) => {
    if (status === "accepte") { setConfirming({ q, lot, nextLabel }); return; }
    engine.setQuoteStatus(project.ref, q.id, status);
  };

  const QuoteGroups = ({ groups, testid, emptyLabel }) => (
    <>
      {groups.length === 0 && <Placeholder testid={`${testid}-empty`}>{emptyLabel}</Placeholder>}
      <div className="space-y-5">
        {groups.map(({ lot, quotes }) => (
          <div key={lot.id} data-testid={`lot-quotes-${lot.id}`}>
            <p className="mb-2 text-sm font-semibold text-navy">{lot.label}</p>
            <ul className="divide-y divide-[#F0F2F3] rounded-xl border border-[#EDF0F1]">
              {quotes.map((q) => {
                const c = engine.company(q.companyId);
                const n = next[q.status];
                const blocked = q.status === "accepte" && !engine.compliance(q.companyId).ok;
                const isConfirming = confirming?.q.id === q.id;
                const pdfUrl = project.documents.find((d) => d.id === q.fileId)?.downloadUrl;
                return (
                  <li key={q.id} data-testid={`quote-${q.id}`} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 text-sm">
                      <p className="font-semibold text-navy">{c?.name}</p>
                      <p className="text-xs text-stone-500">{euro(q.amountHT)} HT {pdfUrl ? <a data-testid={`quote-${q.id}-pdf`} href={pdfUrl} target="_blank" rel="noreferrer" className="ml-1 font-semibold text-petrol underline">Voir le devis</a> : <span className="ml-1 text-stone-400">PDF indisponible</span>}</p>
                    </div>
                    {isConfirming ? (
                      <div data-testid={`quote-${q.id}-confirm`} className="flex flex-col gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-800 sm:flex-row sm:items-center">
                        <span>Confirmer l'attribution du {lot.label} à {c?.name} pour {euro(q.amountHT)} HT ?</span>
                        <div className="flex gap-2">
                          <Btn className="px-3 py-1.5 text-xs" testid={`quote-${q.id}-confirm-yes`} onClick={() => { engine.setQuoteStatus(project.ref, q.id, "accepte"); setConfirming(null); }}>Confirmer</Btn>
                          <Btn variant="ghost" className="px-3 py-1.5 text-xs" testid={`quote-${q.id}-confirm-no`} onClick={() => setConfirming(null)}>Annuler</Btn>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Chip status={q.status} testid={`quote-${q.id}-status`} />
                        {blocked && <Chip status="expire" label="Démarrage bloqué" />}
                        {n && !(q.status === "accepte" && blocked) && (
                          <Btn variant="soft" className="px-3 py-1.5 text-xs" testid={`quote-${q.id}-next`} onClick={() => requestFinalStatus(q, lot, n[0], n[1])}>{n[0] === "accepte" ? "Choisir" : n[1]}</Btn>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="space-y-4">
      <RequestQuoteForm project={project} engine={engine} backend={backend} />
      <Card className="p-5" testid="quotes-list">
        <SectionTitle>Devis par lot</SectionTitle>
        <QuoteGroups groups={travauxGroups} testid="quotes-list" emptyLabel="Aucun devis reçu pour le moment." />
      </Card>
      <Card className="p-5" testid="etudes-list">
        <SectionTitle>Études</SectionTitle>
        <QuoteGroups groups={etudeGroups} testid="etudes-list" emptyLabel="Aucune étude demandée pour le moment." />
      </Card>
      <FinancialRecap project={project} engine={engine} backend={backend} />
    </div>
  );
};

// ---------- Planning : Gantt + décalage avec impacts signalés (pas de déplacement automatique) ----------
export const PlanningTab = ({ project }) => {
  const engine = useEngine();
  const [sel, setSel] = useState(null);
  const [shift, setShift] = useState(0);
  const lot = project.lots.find((l) => l.id === sel);
  const newStart = lot ? addDays(lot.start, shift) : null;
  const newEnd = lot ? addDays(lot.end, shift) : null;
  const impacted = lot ? impactedLots(project, lot.id, newEnd) : [];
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="p-5 lg:col-span-2" testid="planning-gantt">
        <SectionTitle>Planning prévisionnel du chantier</SectionTitle>
        <Gantt project={project} color={PETROL} onSelect={(l) => { setSel(l.id); setShift(0); }} selected={sel} />
      </Card>
      <Card className="p-5" testid="planning-editor">
        <SectionTitle>Réorganiser</SectionTitle>
        {!lot ? (
          <Placeholder>Sélectionnez un lot dans le planning pour modifier ses dates. Vous restez maître des dates : rien n'est déplacé automatiquement.</Placeholder>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="font-semibold text-navy">{lot.label}</p>
            <p className="text-xs text-stone-500">Actuel : {fmtDate(lot.start)} → {fmtDate(lot.end)} · {lot.durationDays} j ouvrés</p>
            <Field label="Décalage (jours)">
              <div className="flex items-center gap-2">
                <Btn variant="ghost" className="px-3 py-1.5" testid="planning-shift-minus" onClick={() => setShift((s) => s - 1)}>−</Btn>
                <input data-testid="planning-shift" type="number" className={`${inputCls} text-center`} value={shift} onChange={(e) => setShift(Number(e.target.value) || 0)} />
                <Btn variant="ghost" className="px-3 py-1.5" testid="planning-shift-plus" onClick={() => setShift((s) => s + 1)}>+</Btn>
              </div>
            </Field>
            <p className="text-xs text-stone-500">Nouveau : <b className="text-navy">{fmtDate(newStart)} → {fmtDate(newEnd)}</b></p>
            {shift !== 0 && (
              <div data-testid="planning-impact" className={`rounded-xl p-3 text-xs ${impacted.length ? "bg-amber-50 text-amber-800" : "bg-sage/10 text-sage"}`}>
                <p className="flex items-center gap-1.5 font-bold"><CalendarClock className="h-3.5 w-3.5" /> Cette modification peut impacter {impacted.length} intervention{impacted.length > 1 ? "s" : ""}.</p>
                <ul className="mt-1.5 space-y-0.5">
                  {impacted.map((i) => <li key={i.id}>• {i.label} ({fmtDate(i.start)}){i.conflict && " — chevauchement"}</li>)}
                </ul>
                {impacted.length > 0 && <p className="mt-1.5 text-[11px]">Les interventions listées ne sont pas déplacées : réorganisez-les ensuite si nécessaire.</p>}
              </div>
            )}
            <Btn testid="planning-apply" disabled={shift === 0} onClick={() => { engine.moveLot(project.ref, lot.id, newStart, newEnd); setShift(0); toast.success("Dates enregistrées — seules les dates de ce lot ont changé."); }}>Valider les nouvelles dates</Btn>
          </div>
        )}
      </Card>
    </div>
  );
};

// ---------- Chantier : avancements à valider + photos & signalements ----------
export const ChantierTab = ({ project }) => {
  const engine = useEngine();
  const [alt, setAlt] = useState({});
  const [pending2Lot, setPending2Lot] = useState({});
  const [form, setForm] = useState({ comment: "", lotId: "", photoIds: [] });
  const pending = project.progressDeclarations.filter((p) => p.status === "a_verifier");
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="p-5" testid="progress-review">
        <SectionTitle right={<Pill tone="copper">{pending.length} à vérifier</Pill>}>Avancements déclarés</SectionTitle>
        {pending.length === 0 && <Placeholder testid="progress-review-empty">Aucun avancement en attente de validation.</Placeholder>}
        <ul className="space-y-3">
          {pending.map((pd) => {
            const lot = project.lots.find((l) => l.id === pd.lotId);
            return (
              <li key={pd.id} data-testid={`progress-${pd.id}`} className="rounded-xl border border-amber-100 bg-amber-50/30 p-4 text-sm">
                <div className="flex items-center justify-between"><p className="font-semibold text-navy">{lot.label} — {engine.company(pd.companyId).name}</p><Chip status="a_verifier" /></div>
                <p className="mt-1 text-xs text-stone-500">{fmtDate(pd.date)} · déclare <b className="text-navy">{pd.declared} %</b> (validé actuel {lot.validatedProgress} %) · {pd.photos} photo{pd.photos > 1 ? "s" : ""}</p>
                {pd.comment && <p className="mt-1 text-xs italic text-stone-500">« {pd.comment} »</p>}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Btn className="px-3 py-1.5 text-xs" testid={`progress-${pd.id}-validate`} onClick={() => engine.reviewProgress(project.ref, pd.id, "validate", pd.declared, "")}><Check className="h-3.5 w-3.5" /> Valider {pd.declared} %</Btn>
                  <input data-testid={`progress-${pd.id}-alt`} type="number" min="0" max="100" placeholder="Autre %" className={`${inputCls} w-24 py-1.5 text-xs`} value={alt[pd.id] ?? ""} onChange={(e) => setAlt({ ...alt, [pd.id]: e.target.value })} />
                  <Btn variant="soft" className="px-3 py-1.5 text-xs" testid={`progress-${pd.id}-propose`} disabled={alt[pd.id] === undefined || alt[pd.id] === ""} onClick={() => engine.reviewProgress(project.ref, pd.id, "propose", Number(alt[pd.id]), `Après visite chantier : ${alt[pd.id]} %`)}>Proposer ce %</Btn>
                  <Btn variant="ghost" className="px-3 py-1.5 text-xs" testid={`progress-${pd.id}-refuse`} onClick={() => engine.reviewProgress(project.ref, pd.id, "refuse", null, "Correction demandée")}><X className="h-3.5 w-3.5" /> Refuser</Btn>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="mt-5"><LotProgressList project={project} mode="partenaire" color={PETROL} testid="chantier-lot-progress" /></div>
        <p className="mt-2 text-[11px] text-stone-400">Barre pleine : avancement validé (officiel). Barre claire : avancement déclaré par l'entreprise.</p>
      </Card>
      <Card className="p-5" testid="observations-list">
        <SectionTitle>Photos & signalements</SectionTitle>
        <div className="space-y-2 rounded-xl bg-petrol-bg p-3">
          <textarea data-testid="obs-form-comment" rows={2} className={inputCls} placeholder="Un commentaire (facultatif pour une simple photo)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          <select data-testid="obs-form-lot" className={inputCls} value={form.lotId} onChange={(e) => setForm({ ...form, lotId: e.target.value })}>
            <option value="">Numéro de lot concerné (facultatif)</option>
            {project.lots.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
          </select>
          <PhotoField engine={engine} project={project} lotId={form.lotId} onChange={(photoIds) => setForm({ ...form, photoIds })} testid="obs-form-photo" />
          <div className="flex flex-wrap gap-2">
            <Btn
              variant="soft" className="px-3 py-1.5 text-xs" testid="obs-form-save-photo"
              onClick={() => {
                if (!form.comment.trim() && form.photoIds.length === 0) return toast.error("Ajoutez une photo ou un commentaire.");
                if (engine.signalObservation(project.ref, "partenaire", "Photo de chantier", form.comment, form.lotId || null, form.photoIds, "photo")) {
                  setForm({ comment: "", lotId: "", photoIds: [] }); toast.success("Photo enregistrée.");
                }
              }}
            >
              Enregistrer la photo
            </Btn>
            <Btn
              className="px-3 py-1.5 text-xs" testid="obs-form-submit"
              onClick={() => {
                if (!form.comment.trim()) return toast.error("Décrivez le problème.");
                if (engine.signalObservation(project.ref, "partenaire", form.comment.slice(0, 60), form.comment, form.lotId || null, form.photoIds, "signalement")) {
                  setForm({ comment: "", lotId: "", photoIds: [] }); toast.success("Signalement créé.");
                }
              }}
            >
              Créer un signalement
            </Btn>
          </div>
        </div>
        <ul className="mt-4 space-y-3">
          {project.observations.map((o) => {
            const kind = o.kind || "signalement";
            return (
              <li key={o.id} data-testid={`obs-${o.id}`} className="rounded-xl border border-[#E3E8E9] p-4 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div><p className="font-semibold text-navy">{kind === "photo" ? "Photo de chantier" : o.title}</p><p className="text-xs text-stone-500">{project.lots.find((l) => l.id === o.lotId)?.label || "Lot non précisé"} · <RoleTag role={o.role} /> {o.author} · {fmtDate(o.date)}</p></div>
                  <Chip status={o.status} testid={`obs-${o.id}-status`} />
                </div>
                {o.comment && <p className="mt-1 text-xs italic text-stone-500">« {o.comment} »</p>}
                {(o.photoIds?.length > 0 || o.afterPhotoIds?.length > 0) && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {o.photoIds?.map((pid) => <PhotoThumb key={pid} id={pid} mode={engine.mode} testid={`obs-${o.id}-photo`} />)}
                    {o.afterPhotoIds?.map((pid) => <PhotoThumb key={pid} id={pid} mode={engine.mode} testid={`obs-${o.id}-after-photo`} />)}
                  </div>
                )}
                <div className="mt-2"><History items={o.history} /></div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {kind === "photo" && o.status === "publiee" && (
                    <Btn variant="ghost" className="px-3 py-1.5 text-xs" testid={`obs-${o.id}-report`} onClick={() => engine.reportObservation(project.ref, "partenaire", o.id, "")}>Signaler un problème</Btn>
                  )}
                  {kind === "signalement" && o.status === "signale" && (
                    <>
                      <select data-testid={`obs-${o.id}-assign`} className={`${inputCls} w-auto py-1.5 text-xs`} value={pending2Lot[o.id] ?? o.lotId ?? ""} onChange={(e) => setPending2Lot({ ...pending2Lot, [o.id]: e.target.value })}>
                        <option value="">Choisir le lot et l'entreprise…</option>
                        {project.lots.filter((l) => l.companyId).map((l) => <option key={l.id} value={l.id}>{`${l.label} — ${engine.company(l.companyId)?.name}`}</option>)}
                      </select>
                      <Btn className="px-3 py-1.5 text-xs" testid={`obs-${o.id}-send`} disabled={!pending2Lot[o.id]} onClick={() => engine.assignObservation(project.ref, o.id, pending2Lot[o.id])}><Send className="h-3.5 w-3.5" /> Envoyer à l'entreprise</Btn>
                    </>
                  )}
                  {kind === "signalement" && o.status === "a_verifier" && (
                    <>
                      <Btn className="px-3 py-1.5 text-xs" testid={`obs-${o.id}-close`} onClick={() => engine.closeObservation(project.ref, o.id, true)}>Valider et clôturer</Btn>
                      <Btn variant="ghost" className="px-3 py-1.5 text-xs" testid={`obs-${o.id}-reopen`} onClick={() => engine.closeObservation(project.ref, o.id, false)}>À reprendre</Btn>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
};

// ---------- Finances : situations par lot, factures à vérifier, honoraires, services Central ----------
export const FinancesTab = ({ project }) => {
  const engine = useEngine();
  const [reason, setReason] = useState({});
  const toReview = project.invoices.filter((i) => i.status === "a_verifier");
  const contested = project.invoices.filter((i) => i.status === "contestee");
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5" testid="invoices-review">
          <SectionTitle right={<Pill tone="copper">{toReview.length} à vérifier</Pill>}>Factures & situations à contrôler</SectionTitle>
          {toReview.length === 0 && <Placeholder testid="invoices-review-empty">Aucune facture en attente.</Placeholder>}
          <ul className="space-y-3">
            {toReview.map((i) => (
              <li key={i.id} data-testid={`invoice-${i.id}`} className="rounded-xl border border-amber-100 bg-amber-50/30 p-4 text-sm">
                <p className="font-semibold text-navy">{INVOICE_TYPES[i.type]} — {project.lots.find((l) => l.id === i.lotId)?.label} · {engine.company(i.companyId).name}</p>
                <p className="text-xs text-stone-500">{i.number} · {fmtDate(i.date)} · {euro(i.amountHT)} HT / {euro(i.amountTTC)} TTC · v{i.version} · <FileText className="inline h-3 w-3" /> {i.file}</p>
                {i.comment && <p className="mt-1 text-xs italic text-stone-500">« {i.comment} »</p>}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Btn className="px-3 py-1.5 text-xs" testid={`invoice-${i.id}-validate`} onClick={() => engine.reviewInvoice(project.ref, i.id, true)}><Check className="h-3.5 w-3.5" /> Valider — visible par le client</Btn>
                  <input data-testid={`invoice-${i.id}-reason`} placeholder="Motif de retour (obligatoire)" className={`${inputCls} flex-1 py-1.5 text-xs`} value={reason[i.id] || ""} onChange={(e) => setReason({ ...reason, [i.id]: e.target.value })} />
                  <Btn variant="ghost" className="px-3 py-1.5 text-xs" testid={`invoice-${i.id}-return`} disabled={!reason[i.id]?.trim()} onClick={() => engine.reviewInvoice(project.ref, i.id, false, reason[i.id])}>Retourner</Btn>
                </div>
              </li>
            ))}
          </ul>
          {contested.length > 0 && (
            <div className="mt-4 rounded-xl border border-red-100 bg-red-50/40 p-4 text-sm" data-testid="invoices-contested">
              <p className="flex items-center gap-1.5 font-bold text-red-700"><AlertTriangle className="h-4 w-4" /> {contested.length} facture{contested.length > 1 ? "s" : ""} contestée{contested.length > 1 ? "s" : ""} par le client</p>
              {contested.map((i) => <p key={i.id} className="mt-1 text-xs text-stone-600">{i.number} — {i.contest.reason} : « {i.contest.comment} »</p>)}
            </div>
          )}
        </Card>
        <Card className="p-5" testid="lot-finances">
          <SectionTitle>Situations par lot (HT)</SectionTitle>
          <ul className="space-y-3">
            {project.lots.filter((l) => l.amountHT > 0).map((l) => {
              const f = lotFinance(project, l);
              return (
                <li key={l.id} data-testid={`lot-finance-${l.id}`} className="rounded-xl bg-petrol-bg p-3 text-xs">
                  <div className="flex items-center justify-between"><p className="text-sm font-semibold text-navy">{l.label}</p><span className="text-stone-500">validé {l.validatedProgress} %</span></div>
                  <dl className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
                    {[["Marché", f.market], ["Déjà facturé", f.already], ["En vérif.", f.pending], ["Nouvelle situation", f.newSituation], ["Cumul", f.cumul], ["Reste", f.reste]].map(([k, v]) => (
                      <div key={k}><dt className="text-stone-400">{k}</dt><dd className="font-semibold text-navy">{euro(v)}</dd></div>
                    ))}
                  </dl>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-[11px] text-stone-400">Avancement physique ≠ montant facturable : la « nouvelle situation » = marché × avancement validé − déjà facturé − en vérification.</p>
        </Card>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="p-5" testid="all-invoices">
          <SectionTitle>Toutes les factures</SectionTitle>
          <ul className="divide-y divide-[#F0F2F3] text-xs">
            {project.invoices.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-2 py-2">
                <span className="min-w-0 truncate"><b className="text-navy">{i.number}</b> · {project.lots.find((l) => l.id === i.lotId)?.label} · {euro(i.amountHT)} HT</span>
                <Chip status={i.status} testid={`all-invoice-${i.id}-status`} />
              </li>
            ))}
          </ul>
        </Card>
        <Card className="p-5" testid="honoraires">
          <SectionTitle>Honoraires MOE (flux B)</SectionTitle>
          <ul className="divide-y divide-[#F0F2F3] text-xs">
            {project.honoraires.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-2 py-2"><span><b className="text-navy">{h.label}</b> · {euro(h.amountHT)} HT · {fmtDate(h.due)}</span><Chip status={h.status} /></li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-stone-400">Échéances configurables selon le contrat Partenaire (signature, conception, consultation, démarrage, étapes, réception).</p>
        </Card>
        <Card className="p-5" testid="central-services">
          <SectionTitle>Services CozyHome Central (flux C)</SectionTitle>
          <ul className="space-y-3 text-xs">
            {project.technicalServices.map((s) => {
              const idx = CENTRAL_FLOW.indexOf(s.centralStatus);
              const nextS = CENTRAL_FLOW[idx + 1];
              const blockedByPayment = nextS === "a_produire" && s.centralStatus !== "paye";
              return (
                <li key={s.id} data-testid={`service-${s.id}`} className="rounded-xl bg-petrol-bg p-3">
                  <div className="flex items-center justify-between"><b className="text-navy">{s.label}</b><Chip status={s.centralStatus} testid={`service-${s.id}-status`} /></div>
                  <p className="mt-1 text-stone-500">Prix client {euro(s.clientPriceHT)} HT · <span className="text-stone-400">coût Central {euro(s.centralCostHT)} HT (interne, jamais visible client)</span></p>
                  {nextS && (
                    <Btn variant="soft" className="mt-2 px-3 py-1.5 text-[11px]" testid={`service-${s.id}-advance`} disabled={blockedByPayment} onClick={() => engine.advanceService(project.ref, s.id, nextS)}>
                      Simuler → {CENTRAL_STATUS[nextS]}
                    </Btn>
                  )}
                  {s.centralStatus === "attente_paiement" && <p className="mt-1 text-[11px] text-amber-700">Paiement complet avant production (simulé, aucun paiement réel).</p>}
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
      <Card className="p-5" testid="audit-card">
        <SectionTitle>Historique (audit trail)</SectionTitle>
        <AuditList ref={project.ref} limit={10} />
      </Card>
    </div>
  );
};

// ---------- Documents : pièces projet + attestations archivées versionnées ----------
const DOCUMENT_CATEGORIES = ["Plan", "Permis / Déclaration", "Note technique", "Photo", "Autre"];

const AddDocumentForm = ({ project, onAdded }) => {
  const engine = useEngine();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(DOCUMENT_CATEGORIES[0]);
  const [visibleToClient, setVisibleToClient] = useState(false);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setTitle(""); setCategory(DOCUMENT_CATEGORIES[0]); setVisibleToClient(false); setOpen(false); };

  const submit = async () => {
    if (!file || !title.trim()) { toast.error("Sélectionnez un fichier et donnez-lui un titre."); return; }
    setBusy(true);
    try {
      await engine.addProjectDocument(project.ref, file, title.trim(), category, visibleToClient);
      toast.success("Document ajouté au projet.");
      onAdded?.();
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'ajout du document.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return <Btn variant="ghost" className="mt-3" testid="project-docs-add" onClick={() => setOpen(true)}>Ajouter un document</Btn>;
  }

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-[#E3E8E9] p-4">
      <Field label="Fichier">
        <input data-testid="project-docs-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-stone-600" />
      </Field>
      <Field label="Titre du document">
        <input data-testid="project-docs-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Plan de masse — version 2" />
      </Field>
      <Field label="Catégorie">
        <select data-testid="project-docs-category" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
          {DOCUMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Visibilité">
        <Segmented
          testid="project-docs-visibility"
          options={[{ value: "internal", label: "Interne" }, { value: "client", label: "Visible client" }]}
          value={visibleToClient ? "client" : "internal"}
          onChange={(v) => setVisibleToClient(v === "client")}
        />
      </Field>
      <div className="flex gap-2">
        <Btn testid="project-docs-submit" disabled={busy} onClick={submit}>Ajouter</Btn>
        <Btn variant="ghost" testid="project-docs-cancel" onClick={reset}>Annuler</Btn>
      </div>
    </div>
  );
};

export const DocumentsEngineTab = ({ project }) => {
  const engine = useEngine();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="lg:col-span-2"><ContractsPanel project={project} /></div>
      <Card className="p-5" testid="project-docs">
        <SectionTitle>Documents du projet</SectionTitle>
        <ul className="divide-y divide-[#F0F2F3] text-sm">
          {project.documents.map((d) => (
            <li key={d.id} data-testid={`project-doc-${d.id}`} className="flex items-center justify-between gap-2 py-2.5">
              <span><b className="text-navy">{d.label}</b> <span className="text-xs text-stone-500">· {d.category} · {fmtDate(d.date)}</span></span>
              <span className="flex items-center gap-2">
                <a data-testid={`project-doc-${d.id}-download`} href={d.downloadUrl} target="_blank" rel="noreferrer" className="text-xs font-semibold text-petrol underline">Télécharger</a>
                <Pill tone={d.visibleToClient ? "sage" : "stone"}>{d.visibleToClient ? "Visible client" : "Interne"}</Pill>
              </span>
            </li>
          ))}
        </ul>
        <AddDocumentForm project={project} />
      </Card>
      <Card className="p-5" testid="archived-attestations">
        <SectionTitle><span className="inline-flex items-center gap-2"><Archive className="h-4 w-4 text-petrol" /> Attestations archivées du chantier</span></SectionTitle>
        <ul className="space-y-2 text-xs">
          {project.archivedAttestations.map((a) => {
            const c = engine.company(a.companyId);
            const current = c.documents.filter((d) => d.type === a.type).sort((x, y) => y.version - x.version)[0];
            return (
              <li key={a.id} data-testid={`archive-${a.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E3E8E9] p-3">
                <span><b className="text-navy">{c.name}</b> · {COMPANY_DOC_TYPES[a.type]} · <span className="font-mono">v{a.version}</span> · valable jusqu'au {fmtDate(a.validTo)} · lots {a.lotIds.join(", ")}</span>
                <span className="flex items-center gap-2 text-stone-400">archivée le {fmtDate(a.archivedAt)}{current && current.version > a.version && <Pill tone="petrol">Nouvelle version v{current.version} déposée par l'entreprise — copie chantier conservée</Pill>}</span>
              </li>
            );
          })}
        </ul>
        <p className="mt-2 text-[11px] text-stone-400">Copie versionnée des attestations utilisées pour ce chantier : jamais remplacée silencieusement.</p>
      </Card>
    </div>
  );
};

export const EchangesTab = ({ project, role = "partenaire", companyId }) => {
  const engine = useEngine();
  const [text, setText] = useState("");
  return (
    <Card className="p-5" testid="messages">
      <SectionTitle>Échanges</SectionTitle>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (!text.trim()) return; engine.postMessage(project.ref, role, text.trim(), companyId); setText(""); }}>
        <input data-testid="message-input" className={inputCls} placeholder="Écrire un message…" value={text} onChange={(e) => setText(e.target.value)} />
        <Btn type="submit" testid="message-send"><Send className="h-4 w-4" /></Btn>
      </form>
      <ul className="mt-4 space-y-2 text-sm">
        {project.messages.map((m) => (
          <li key={m.id} data-testid={`message-${m.id}`} className="rounded-xl bg-petrol-bg p-3"><p className="flex items-center gap-2 text-xs text-stone-500"><RoleTag role={m.role} /> {m.author} · {fmtDate(m.date)}</p><p className="mt-1 text-navy">{m.text}</p></li>
        ))}
      </ul>
    </Card>
  );
};

export const ttcOf = ttc;
